import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PageviewIcon from "@mui/icons-material/Pageview";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";

import toast from "react-hot-toast";

import api from "services/api";
import moment from "moment";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slider from "@mui/material/Slider";

import Tooltip from "@mui/material/Tooltip";
import DataTable from "examples/Tables/DataTable";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UnionwearLayout from "pages/unionwear/layout";
import { useNavigate } from "react-router-dom";
import ImportExcelModal from "pages/unionwear/components/ImportExcelModal";
import ProductInformationModal from "pages/unionwear/components/ProductInformationModal";
import Prompt from "components/UpStyled/Prompt";
import { DataGrid } from "@mui/x-data-grid";
import { nanoid } from "nanoid";
import MuiPagination from "@mui/material/Pagination";
import { styled } from "@mui/material/styles";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import { useParams } from "react-router-dom";

import SketchDropDownMenu from "pages/unionwear/components/SketchDropDownMenu";
import { sketch_images, viewpointOptions } from "utils/unionwear-data";
import * as XLSX from "xlsx";

const hatTypeOptions = [
  {
    label: "Baseball Cap",
    value: "baseball cap",
  },
  {
    label: "Sun Visor",
    value: "sun visor",
  },
  {
    label: "Camper Cap",
    value: "camper cap",
  },
];

const modelList = [
  { value: "ripstop_clean", label: "ripstop" },
  { value: "duck_clean", label: "duck" },
  { value: "wool_serge_clean", label: "wool serge" },
  { value: "cotton_spandex_clean", label: "cotton spandex" },
  { value: "diamond_knit_clean", label: "diamond knit" },
  { value: "chino_clean", label: "chino" },
  { value: "poly_cotton_twill_clean", label: "polycotton twill" },
  { value: "mesh_clean", label: "mesh" },
  { value: "spacer_mesh_clean", label: "spacer mesh" },
];

const defaultJobConf = {
  batch_size: 2,
  hat_type: hatTypeOptions[0],
  conditioning_scale: 1.1,
  guidance_scale: 8.0,
};

const ImportProductPage = () => {
  const [projectName, setProjectName] = useState("Task");

  const [products, setProducts] = useState([]);
  const [aiResults, setAiResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState();
  const [importExcelVisible, setImportExcelVisible] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productDetailVisible, setProductDetailVisible] = useState(false);
  const [deleteProductlVisible, setDeleteProductlVisible] = useState(false);

  const [jobconf, setJobconf] = useState(defaultJobConf);
  const [viewpoint, setViewpoint] = useState("3q");

  const params = useParams();
  var uwproject_id = params?.id;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();
  const uploadExcelRef = useRef(null);
  const account = useContext(AccountContext);

  useEffect(() => {
    if (uwproject_id) {
      retrieveProject(uwproject_id);
    }
  }, [uwproject_id]);

  const retrieveProject = async (id) => {
    try {
      const { data } = await api.uwProject.retrieve(id);
      let project = data?.uProject;
      setProjectName(project?.name);
      setAiResults(project?.products?.ai_result);
      let productData = project?.products?.data || [];
      if (productData.length > 0) {
        setProducts(productData);

        let hat_type = hatTypeOptions.find(
          (x) => x.value == productData[0]?.config?.[0]?.task_meta?.hat_type
        );

        let batch_size = productData[0]?.config?.[0]?.task_meta?.batch_size || 2;
        let view_point = productData[0]?.config?.[0]?.task_meta?.viewpoint || "3q";
        let oldJobConf = { batch_size: batch_size, hat_type: hat_type };

        let is_customize_sketch = productData[0]?.config?.[0]?.task_meta?.is_customize_sketch;
        let viewpointOption = viewpointOptions.find((x) => x.value == view_point);
        if (viewpointOption?.["customized_options"]) {
          let customizeSketchOption = viewpointOption["customized_options"].find(
            (x) => x.src == productData[0]?.config?.[0]?.task_meta?.conditional
          );

          oldJobConf["customize_sketch"] = {};
          oldJobConf["customize_sketch"][view_point] = customizeSketchOption;
        }
        setViewpoint(view_point);

        setJobconf(oldJobConf);
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to retrieve project list!");
    }
  };

  const findSketchUrl = (viewpoint, code) => {
    let names = sketch_images[viewpoint];
    if (viewpoint == "front" || viewpoint == "3q") {
      for (let i = 0; i < names.length; i++) {
        let matched = true;
        for (let j = 0; j < 4; j++) {
          //iterater each letter
          if (names[i][j] == code[j] || names[i][j] == "X") {
            //X represent "*"
          } else {
            matched = false;
          }
        }

        if (matched) {
          if (names[i].length == 4) {
            return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/${names[i]}.png`;
          } else if (names[i].length == 5 && names[i][4] == code[5]) {
            return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/${names[i]}.png`;
          }
        }
      }

      //return default
      if (viewpoint == "front") {
        return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/6XXUO.png`;
      } else {
        return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/5XXL.png`;
      }
    } else if (viewpoint == "side") {
      if (code[5] == "E") {
        return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/E.png`;
      } else {
        return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/O.png`;
      }
    } else if (viewpoint == "back") {
      for (let i = 0; i < names.length; i++) {
        //iterater each letter
        if (names[i][0] == code[4] && names[i][1] == code[5]) {
          return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/${names[i]}.png`;
        }
      }

      //return default
      return `https://upstyledus.oss-us-east-1.aliyuncs.com/unionwear_resource/sketch_images/${viewpoint}/FO.png`;
    }
  };

  const handleImportProduct = async (data, name) => {
    try {
      if (!name) {
        name = nanoid();
      }

      //add ai task config to each product
      let taskData = [];

      const TASK_TYPE = "unionwear.xl.canny";

      for (let i = 0; i < data.length; i++) {
        let adapter_names = [];

        let backs_and_sides_fabric = data[i].content.backs_and_sides_fabric || "";
        let front_panels_fabric = data[i].content.front_panels_fabric || "";
        let visor_fabric = data[i].content.visor_fabric || "";

        backs_and_sides_fabric = backs_and_sides_fabric.toLowerCase();
        front_panels_fabric = front_panels_fabric.toLowerCase();
        visor_fabric = visor_fabric.toLowerCase();

        backs_and_sides_fabric = backs_and_sides_fabric.replaceAll("polycotton", "poly cotton");
        front_panels_fabric = front_panels_fabric.replaceAll("polycotton", "poly cotton");
        visor_fabric = visor_fabric.replaceAll("polycotton", "poly cotton");

        backs_and_sides_fabric = backs_and_sides_fabric.replaceAll("royal", "royal blue");
        front_panels_fabric = front_panels_fabric.replaceAll("royal", "royal blue");
        visor_fabric = visor_fabric.replaceAll("royal", "royal blue");

        backs_and_sides_fabric = backs_and_sides_fabric.replaceAll("duck", "duck fabric");
        front_panels_fabric = front_panels_fabric.replaceAll("duck", "duck fabric");
        visor_fabric = visor_fabric.replaceAll("duck", "duck fabric");

        let hat_type = jobconf.hat_type?.value;

        let prompt = "";
        let strap_type = {
          T: "polo strap",
          L: "leather strap",
          P: "plastic strap",
          //E: "", //"stretch fit back"
          F: "buckle strap",
          V: "velcro strap",
          W: "webbing strap",
        };

        let isTwoTone =
          backs_and_sides_fabric != front_panels_fabric || backs_and_sides_fabric != visor_fabric;
        let capShape = jobconf?.customize_sketch?.[viewpoint]?.shape
          ? jobconf?.customize_sketch?.[viewpoint]?.shape
          : ""; //five panel/six panel
        if (isTwoTone) {
          adapter_names.push("two-tone-mix");

          if (viewpoint == "front") {
            prompt = `a two tone ${
              capShape ? capShape : ""
            } ${hat_type} with ${visor_fabric} brim, ${front_panels_fabric} front panel, ${backs_and_sides_fabric} back panel. clear background.`;
          } else if (viewpoint == "3q") {
            prompt = `a two tone ${
              capShape ? capShape : ""
            } ${hat_type} with ${visor_fabric} brim, ${front_panels_fabric} front panel, ${backs_and_sides_fabric} back panel. clear background.`;
          } else if (viewpoint == "side") {
            prompt = `a side view of a two tone ${hat_type}, ${backs_and_sides_fabric} back panel, ${visor_fabric} brim, ${front_panels_fabric} front panel, clear background.`;
          } else if (viewpoint == "back") {
            prompt = `back view of a ${backs_and_sides_fabric} ${hat_type} ${
              strap_type?.[data[i]["code"]?.[4]] ? "with " + strap_type[data[i]["code"]?.[4]] : ""
            } in a clear background.`;
          }
        } else {
          if (viewpoint == "front") {
            prompt = `a ${front_panels_fabric} ${
              capShape ? capShape : ""
            } ${hat_type} in a clear background.`;
          } else if (viewpoint == "3q") {
            prompt = `a ${front_panels_fabric} ${
              capShape ? capShape : ""
            } ${hat_type} in a clear background.`;
          } else if (viewpoint == "side") {
            prompt = `side view of a ${front_panels_fabric} ${hat_type} in a clear background.`;
          } else if (viewpoint == "back") {
            prompt = `back view of a ${front_panels_fabric} ${hat_type} ${
              strap_type?.[data[i]["code"]?.[4]] ? "with " + strap_type[data[i]["code"]?.[4]] : ""
            } in a clear background.`;
          }
        }
        if (hat_type == "sun visor" || hat_type == "camper cap") {
          if (viewpoint == "front" || viewpoint == "3q") {
            prompt = `a ${front_panels_fabric} ${hat_type} in a clear background.`;
          } else if (viewpoint == "side") {
            prompt = `side view of a ${front_panels_fabric} ${hat_type} in a clear background.`;
          } else if (viewpoint == "back") {
            prompt = `back view of a ${front_panels_fabric} ${hat_type} ${
              strap_type?.[data[i]["code"]?.[4]] ? "with " + strap_type[data[i]["code"]?.[4]] : ""
            } in a clear background.`;
          }
        }

        let control_image = jobconf?.customize_sketch?.[viewpoint]?.src
          ? jobconf?.customize_sketch[viewpoint].src
          : findSketchUrl(viewpoint, data[i]["code"]);

        let hasEyelets = jobconf?.customize_sketch?.[viewpoint]?.eyelet || false;
        let negative_prompt =
          "dent, lumpy, unrealistic, embroidered logo, background, mannequin, tag, gradient color, patchy, irregular pattern.";
        if (!hasEyelets) {
          negative_prompt = "eyelets, " + negative_prompt;
        }

        prompt =
          prompt +
          " detailed stitching, high-quality fabric, well-formed, clean separation, realistic, professional design.";

        let fabric_model = new Set();
        let all_fabric_str = backs_and_sides_fabric + front_panels_fabric + visor_fabric;

        all_fabric_str = all_fabric_str.replaceAll(" ", "");
        for (let j = 0; j < modelList.length; j++) {
          let label = modelList[j].label;
          label = label.replaceAll(" ", "");

          if (all_fabric_str.includes(label)) {
            fabric_model.add(modelList[j].value);
          }
        }
        let model_arr = Array.from(fabric_model);

        for (let model of model_arr) {
          adapter_names.push(model);
        }

        const controlnet_conditioning_scale = Number(jobconf.conditioning_scale);
        const guidance_scale = Number(jobconf.guidance_scale);

        var config_front = {
          task_type: TASK_TYPE,
          owner_id: account?.id,
          task_meta: {
            adapter_names: adapter_names,
            extract_sketch: false,
            product_id: data[i].id,
            conditional: control_image,
            task_type: TASK_TYPE,
            prompt: prompt,
            weights: isTwoTone ? [0.7, 0.35] : [],
            negative_prompt: negative_prompt,
            batch_size: jobconf.batch_size || 2,
            num_inference_steps: 30,
            controlnet_conditioning_scale: isNaN(controlnet_conditioning_scale)
              ? 8.0
              : controlnet_conditioning_scale,
            guidance_scale: isNaN(guidance_scale) ? 8.0 : guidance_scale,
            start_at: new Date(),
            viewpoint: viewpoint,
            code: data[i].code,
            hat_type: jobconf.hat_type?.value,
            is_customize_sketch: jobconf?.customize_sketch?.[viewpoint]?.src ? true : false,
          },
        };

        taskData.push({
          ...data[i],
          config: [config_front],
        });
      }
      if (uwproject_id) {
        await api.uwProject.update(uwproject_id, {
          name: name,
          status: "init",
          products: { data: taskData, ai_result: aiResults },
        });
      } else {
        await api.uwProject.create({ name: name, status: "init", products: { data: taskData } });
      }

      handleBackToTaskPage();
    } catch (err) {
      console.log(err);
      toast.error("Failed to import products!");
    }
  };

  const getProductTableRow = (item) => {
    const images = item?.content?.images || [];

    let rowMap = {
      id: item.id,
      name: item?.name,
      code: item?.code,
      images: images,
      backs_and_sides_fabric: item?.content?.backs_and_sides_fabric,
      visor_fabric: item?.content?.visor_fabric,
      front_panels_fabric: item?.content?.front_panels_fabric,
      created_at: item.created_at,
      updated_at: item.updated_at,
      action: item,
    };

    return rowMap;
  };

  const columns = [
    {
      headerName: "Image",
      field: "images",
      renderCell: ({ value, row }) => {
        return (
          <SuiBox display="flex" alignItems="center" justifyContent="space-between">
            {value && value.length > 0 ? (
              <img src={value[0]["url"]} style={{ width: 50, height: 50 }} />
            ) : (
              <SuiBox minWidth={50} minHeight={50} />
            )}
          </SuiBox>
        );
      },
    },
    {
      headerName: "Name",
      field: "name",
      renderCell: ({ value, row }) => {
        return <div style={{ maxWidth: 360 }}>{value}</div>;
      },
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Code",
      field: "code",
      minWidth: 150,
    },
    {
      headerName: "Front panel fabric",
      field: "front_panels_fabric",
      minWidth: 200,
    },
    {
      headerName: "Backs and sides fabric",
      field: "backs_and_sides_fabric",
      minWidth: 200,
    },
    {
      headerName: "Visor fabric",
      field: "visor_fabric",
      minWidth: 200,
    },
    {
      headerName: "More",
      field: "action",
      renderCell: ({ value, row }) => (
        <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Tooltip title="View detail">
            <SuiBox>
              <ManageSearchIcon
                fontSize={"small"}
                color="dark"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProduct(value);
                  setProductDetailVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
          |
          <Tooltip title="Delete">
            <SuiBox>
              <DeleteForeverRoundedIcon
                fontSize={"small"}
                color="error"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProduct(value);
                  setDeleteProductlVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
        </SuiBox>
      ),
    },
  ];

  const rows = products.map((item) => getProductTableRow(item));

  const handleBackToTaskPage = () => {
    navigate("/unionwear/tasks");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      setExcelData(workbook);
      setImportExcelVisible(true);
      setLoading(false);
    };

    reader.onerror = () => {
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const formatData = (data, headerMap, key) =>
    headerMap[key] != undefined ? data[headerMap[key]] : undefined;

  const handleImportExcelMaterial = async (data) => {
    if (!data || data.length < 0) {
      handleClose();
      return;
    }

    let headerMap = {};
    for (let i = 0; i < data[0].length; i++) {
      let str = String(data[0][i]);
      let key = str.trim().toLowerCase().replaceAll("'", "").replaceAll(" ", "_");
      if (key == "description") {
        headerMap["name"] = i;
      } else if (key == "sku") {
        headerMap["code"] = i;
      } else if (key == "#_eyelets") {
        headerMap["num_eyelets"] = i;
      } else if (key == "#_visor_stitches") {
        headerMap["num_visor_stitches"] = i;
      } else if (key == "front_panels_fabric" || key == "front_panel_fabric") {
        headerMap["front_panels_fabric"] = i;
      } else {
        headerMap[key] = i;
      }
    }

    let rows = [];
    let metaKeys = [
      "crown_shape",
      "front_panels_fabric",
      "backs_and_sides_fabric",
      "visor_fabric",
      "button_fabric",
      "closure_type",
      "closure_color",
      "num_eyelets",
      "crown_stitch_color",
      "visor_stitch_color",
      "baseball_stitch_color",
      "eyelet_color",
      "num_visor_stitches",
      "visor_curve_or_flat",
      "sandwich_color",
      "trim_color",
      "description",
    ];

    let imageKeys = ["3q_image", "back_image", "virtual", "swatches"];

    for (let i = 1; i < data.length; i++) {
      let name = formatData(data[i], headerMap, "name");
      if (!name) {
        continue;
      }

      let images = imageKeys.map((x) => ({
        url: formatData(data[i], headerMap, x),
        type: "image/jpg",
        name: x,
      }));

      let temp = {};

      for (let j = 0; j < metaKeys.length; j++) {
        temp[metaKeys[j]] = formatData(data[i], headerMap, metaKeys[j]);
      }

      let payload = {
        id: nanoid(),
        name: name,
        code: formatData(data[i], headerMap, "code"),
        content: {
          ...temp,
          images: images,
        },
      };

      if (i != 0) {
        rows.push(payload);
      }
    }

    try {
      setProducts(rows);
      //const created = await api.uwProduct.bulkCreate({ data: rows });
      toast.success("Data has been imported Successfully");
    } catch (err) {
      console.log(err);
      toast.error(err?.message ? err?.message : "Failed to import data!");
    }
  };

  const handleDeleteProduct = (product) => {
    const newProducts = products.filter((x) => x.code != product.code);
    setProducts(newProducts);
    setDeleteProductlVisible(false);
  };

  return (
    <SuiBox>
      <Backdrop sx={{ zIndex: (theme) => 99999 }} open={loading}>
        <CircularProgress color="white" sx={{ mr: 1 }} size={32} />{" "}
        <SuiTypography variant="h6" color="white">
          Uploading......
        </SuiTypography>
      </Backdrop>

      <UnionwearLayout>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" gap={1} py={2}>
          <SuiButton
            onClick={() => {
              handleBackToTaskPage();
            }}
            variant={"text"}
            color="dark"
          >
            <ArrowBackIcon sx={{ mr: 0.5, width: 18, height: 18 }} />
            Back
          </SuiButton>
          <SuiTypography variant="h5" color="dark" sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }}>
            {"Create Task"}
          </SuiTypography>
          <div />
        </SuiBox>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleImportProduct(products, projectName);
          }}
        >
          <SuiBox
            display={"flex"}
            justifyContent="space-between"
            alignItems="flex-end"
            gap={2}
            px={2}
          >
            <SuiBox display="flex" flexWrap="wrap" gap={2}>
              <SuiBox>
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    required={true}
                  >
                    Task Name:
                  </SuiTypography>
                </SuiBox>
                <SuiInput
                  onChange={(e) => setProjectName(e.target.value)}
                  value={projectName}
                  sx={{ width: 200 }}
                  required
                />
              </SuiBox>
              <SuiBox>
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    required={true}
                  >
                    Hat Type:
                  </SuiTypography>
                </SuiBox>
                <SuiBox sx={{ width: 160 }}>
                  <SuiSelect
                    value={jobconf.hat_type}
                    options={hatTypeOptions}
                    onChange={(opt) => setJobconf((prev) => ({ ...prev, hat_type: opt }))}
                  />
                </SuiBox>
              </SuiBox>

              <div>
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography component="label" variant="caption" fontWeight="bold">
                    Batch Size:
                  </SuiTypography>
                </SuiBox>
                <SuiBox sx={{ width: 100 }}>
                  <Slider
                    aria-label="Batch_Size"
                    defaultValue={2}
                    valueLabelDisplay="auto"
                    step={1}
                    marks
                    min={1}
                    max={4}
                    onChange={(e) =>
                      setJobconf((prev) => ({ ...prev, batch_size: e.target.value }))
                    }
                    value={jobconf.batch_size}
                  />
                </SuiBox>
              </div>
              <div>
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <Tooltip title="Determines how much weight to assign to the sketch">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Conditioning Scale(0-3)
                    </SuiTypography>
                  </Tooltip>
                </SuiBox>
                <SuiInput
                  placeholder="1.1"
                  type={"number"}
                  inputProps={{
                    max: 3,
                    min: 0,
                    step: 0.05,
                  }}
                  onChange={(e) =>
                    setJobconf((prev) => ({ ...prev, conditioning_scale: e.target.value }))
                  }
                  value={jobconf.conditioning_scale}
                />
              </div>
              <div>
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <Tooltip title="Determines how much weight to assign to the prompt">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Guidance Scale(0-20)
                    </SuiTypography>
                  </Tooltip>
                </SuiBox>
                <SuiInput
                  placeholder="8.0"
                  type={"number"}
                  inputProps={{
                    max: 20,
                    min: 0,
                    step: 0.5,
                  }}
                  onChange={(e) =>
                    setJobconf((prev) => ({ ...prev, guidance_scale: e.target.value }))
                  }
                  value={jobconf.guidance_scale}
                />
              </div>
            </SuiBox>

            <SuiBox>
              <SuiButton
                variant="gradient"
                color={"info"}
                id="basic-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
                fontWeight="bold"
              >
                Import
              </SuiButton>{" "}
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem
                  onClick={() => {
                    if (uploadExcelRef?.current) {
                      uploadExcelRef.current.click();
                      uploadExcelRef.current.value = null;
                    }
                    handleClose();
                  }}
                >
                  Upload Excel
                </MenuItem>
                {/* <MenuItem onClick={handleClose}>Import Itemcodes</MenuItem> */}
              </Menu>
            </SuiBox>
          </SuiBox>

          <SuiBox px={2} mt={1}>
            <SuiBox ml={0.5} lineHeight={0} display="inline-block">
              <SuiTypography component="label" variant="caption" fontWeight="bold" required={true}>
                Viewpoint:
              </SuiTypography>
            </SuiBox>
            <SuiBox sx={{ ml: 2 }}>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={viewpoint}
                onChange={(e) => {
                  setViewpoint(e.target.value);
                }}
              >
                {viewpointOptions.map((item) => (
                  <SuiBox display="flex" alignItems="center" sx={{ minHeight: 50, minWidth: 100 }}>
                    <FormControlLabel
                      value={item.value}
                      control={<Radio />}
                      label={item.label}
                      sx={{ pr: 0 }}
                    />{" "}
                    {viewpoint == item.value ? (
                      <SuiBox pr={2}>
                        <Tooltip title="click to customize sketch">
                          <SketchDropDownMenu
                            sketch={jobconf?.customize_sketch?.[viewpoint]}
                            onClick={(sketch) => {
                              let oldSketch = jobconf?.customize_sketch || {};
                              oldSketch[viewpoint] = sketch;
                              setJobconf((prev) => ({ ...prev, customize_sketch: oldSketch }));
                            }}
                            sketchOptions={item.customized_options || []}
                          />
                        </Tooltip>
                      </SuiBox>
                    ) : null}
                  </SuiBox>
                ))}
              </RadioGroup>
            </SuiBox>
          </SuiBox>

          <SuiBox mt={1}>
            {rows && rows.length > 0 ? (
              <SuiBox sx={{ p: 3, height: 600, width: "100%" }}>
                <StyledDataGrid
                  rows={rows}
                  columns={columns}
                  initialState={{
                    pagination: {
                      pageSize: 25,
                    },
                  }}
                />
              </SuiBox>
            ) : null}
          </SuiBox>
          <SuiBox justifyContent="center" display="flex" pb={2}>
            <SuiButton
              size="medium"
              variant="gradient"
              color="dark"
              type="submit"
              disabled={!products || products.length === 0}
            >
              {uwproject_id ? "Update" : "Create"}
            </SuiButton>
          </SuiBox>
        </form>

        {importExcelVisible && excelData ? (
          <ImportExcelModal
            handleClose={() => setImportExcelVisible(false)}
            visible={importExcelVisible}
            handleOk={handleImportExcelMaterial}
            excelData={excelData}
          />
        ) : null}

        {productDetailVisible && selectedProduct ? (
          <ProductInformationModal
            handleClose={() => setProductDetailVisible(false)}
            visible={productDetailVisible}
            product={selectedProduct}
          />
        ) : null}

        {deleteProductlVisible && selectedProduct ? (
          <Prompt
            heading="Delete Product"
            text={`Are you sure you want to delete: ${selectedProduct.name}`}
            visible={deleteProductlVisible}
            okText="Delete"
            okColor="error"
            handleOk={() => handleDeleteProduct(selectedProduct)}
            handleClose={() => setDeleteProductlVisible(false)}
          />
        ) : null}

        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          ref={uploadExcelRef}
          style={{ display: "none" }}
        />
      </UnionwearLayout>
    </SuiBox>
  );
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiInputBase-root": {
    width: "auto !important",
  },
}));

export default ImportProductPage;
